import { DateAdapter } from './../../shared/adapters/date-adapter';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, throwError } from 'rxjs';
import { environment as env } from '../../../environments/environment';
import { type Coordinate } from '../../shared/models/geolocation.model';
import { Pagination } from '../../shared/models/pagination.model';
import { withDateAdapter, withDateAdapterPage } from '../../shared/rxjs/with-date-adapter';
import { type Location } from './../../shared/models/location.model';

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private readonly endpoint = `${env.baseAPIUrl}/localization`;

  private readonly dateFields = ['createdAt', 'updatedAt', 'deletedAt'];
  private readonly transformDate = withDateAdapter<Location>(this.dateFields);
  private readonly transformDatePage = withDateAdapterPage<Pagination<Location>>(this.dateFields);

  constructor(private readonly http: HttpClient) { }

  getLocationById(locationId: string): Observable<Location> {
    return this.http.get<Location>(`${this.endpoint}/${locationId}`)
      .pipe(this.transformDate);
  }

  paginateLocationByPetId(petId: string, page: number = 0, pageSize: number = 10): Observable<Pagination<Location>> {
    return this.http.get<Pagination<Location>>(`${this.endpoint}/pet/${petId}?page=${page}&pageSize=${pageSize}`)
      .pipe(this.transformDatePage);
  }

  paginateRecentLocationByUserId(userId: string, page: number = 0, pageSize: number = 10): Observable<Pagination<Location>> {
    return this.http.get<Pagination<Location>>(`${this.endpoint}/recents/${userId}?page=${page}&pageSize=${pageSize}`)
      .pipe(this.transformDatePage);
  }

  markLocationAsSeen(locationId: string): Observable<void> {
    return this.http.post<void>(`${this.endpoint}/viewed/${locationId}`, {});
  }

  createLocation(location: Location): Observable<Location> {
    return this.http.post<Location>(`${this.endpoint}`, DateAdapter.toAPI(location, this.dateFields))
      .pipe(this.transformDate);
  }

  deleteLocation(locationId: string): Observable<void> {
    return this.http.delete<void>(`${this.endpoint}/${locationId}`);
  }

  getCurrentLocation(): Observable<Coordinate> {
    return new Observable<Coordinate>(observer => {
      if (!navigator.geolocation) {
        observer.error('Geolocation is not supported by the browser!');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          observer.next({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          observer.complete();
        },
        (error) => {
          observer.error(error.message);
        }
      );
    }).pipe(
      catchError((error) => {
        console.error('Could not receive location:', error);
        return throwError(() => new Error(error));
      })
    );
  }
}
